import { Classes, ContextMenuTarget, Intent, Menu, MenuDivider, MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import autobind from "autobind-decorator";
import last from 'lodash/last';
import isEqual from 'lodash/isEqual';
import queryString from 'query-string';
import React from "react";
import { connect } from "react-redux";
import { Registry } from "react-registry";
import { bindActionCreators } from "redux";

import * as activeActions from "core/actions/activeActions";
import * as configActions from "core/actions/configActions";
import * as editActions from "core/actions/editActions";
import * as paramsActions from "core/actions/paramsActions";
import * as userActions from "core/actions/userActions";
import AncestorProvider from "core/components/editor/AncestorProvider";
import { EDIT_NAME } from "core/components/editor/ConfigForm";
import PanelGeneralConfig from "core/components/editor/panels/PanelGeneralConfig";
import Label from "core/components/label/Label";
import LabelProvider from "core/components/label/LabelProvider";
import initialState from "core/reducers/initialState";
import Class from "core/util/Class";
import Logger from "core/util/Logger";
import { iDef, nDef } from "core/util/TypeUtils";
import { BoxVariant, BoxView } from "../../common/views/box/Box.view";
import ErrorBoundary from "../components/ErrorBoundry";
import { ErrorSource } from "../service/logger/ILoggerService";
import { PropertyProvider } from "../util/property/PropertyProvider";
import { getPanelViewKey } from "./View";
import { setPageHeading } from "common/fragments/PageHeading";
import PanelUtil from "core/util/PanelUtil";
// TODO get this from registry or move into core
import WindowSizeCondition from "common/form/fields/condition/conditionTypes/WindowSizeCondition.reg";
import { toAttributesObject } from "common/form/fields/KeyValueListField";
import * as cartAction from "core/actions/cartActions";
import * as pageSizeActions from "core/actions/pageSizeActions";

/**
 * @overview: Core component for centralizing how data is encapsulated.
 * More documentation coming soon.
 *
 * @author: jesposito
 */

// tslint:disable:max-classes-per-file

/**
 * Arguments passed into the @Panel decorator
 *
 * Example: @Panel({id: "foo"})
 */
interface IPanelArgs<T, S> {
	id: string;
	configClass?: T;
	conditions?: object;
	pageParams?: boolean;
	sharedState?: S;
	stackRoot?: boolean;
	labelProvider?: LabelProvider<any>;
	group?: string;
}

/**
 * Properties passed down into the panel component.
 *
 * Example: class FooPanel extends React.Component<IPanelProps>> {
 *
 */
export interface IPanelProps<T, S = any> {

	// id the panel is registered with
	id: string;

	// config object for the panel
	config?: T & PanelGeneralConfig;

	// list of registered views in use
	active?: object;

	// the config instance. not to be confused with the instance of the panel component
	instance: string;

	// actions that impact global state
	actions?: PanelActions;

	// TODO typed
	pageParams?: any;

	// the panel's parent
	containerInstance?: string;

	// all the parents
	ancestors?: string[];

	// not the parent?
	parent?: string;

	// is already active registered
	isRegistered?: boolean;

	// state that is accessible to every instance of a panel compoent that has the same config instnace.
	globalState?: S;

	editingEnabled?: boolean;

	language?: string;

	locale?: string;

	timeZone?: string;

	searchText?: string;

	searchIncludesTag?:boolean;

	trackPaginationVal?: any;

	cartItems?: any;

	quantityInCart?: any;

	discountResponse?: any,

	cartUserDetails?: any,

	shippableUserDetails?: any,

	cartItemsGroupBuy?: boolean,

	itemsToShow?: number;

	forumId?: string;
}

export type PanelActions<T = any> = typeof paramsActions & typeof userActions & typeof cartAction & typeof pageSizeActions & {
	setGlobalState: (state: T) => void;
};

/**
 *
 *
 * @param {IPanelArgs<any, any>} args
 * @returns {any}
 * @constructor
 */
export function Panel(args?: IPanelArgs<any, any>): any {
	return (panel: Class<any>) => {

		// wrap the supplied panel class with the redux connection and with <PanelWrapper>
		const connected = connect((state, ownProps) => (
			mapPanelProps(state, ownProps, args, panel)
		), mapDispatchToProps)(PanelWrapper);

		Logger.debug("Registering panel: %s", args.id);

		// register the panel
		Registry.register(connected, {id: args.id, registry: "panels", conditions: args.conditions});

		// register the config class for the panel
		iDef(args.configClass, (it) => {
			Logger.debug("Registering config: %s, for: %s", args.configClass.name, args.id);

			Registry.register(it, {id: args.id, registry: "panels:config"});
		});

		// register label provider
		iDef(args.labelProvider, (it) => {
			Logger.debug("Registering label provider for %s", args.id);

			LabelProvider.registerProvider(args.id, it);
		});

		return connected;
	};
}

export interface IPanelContext {
	panelId: string;
	instance: string;
	registerView: (viewKey: string, viewId: string) => void;
	editingEnabled?: boolean;
}

export const PanelContext = React.createContext<IPanelContext>(undefined);

const mapPanelProps = (state, ownProps: any, args: IPanelArgs<any, any>, panel: any): any => {

	const instance = ownProps.instance;
	let config = state.config.panels[instance];

	if(nDef(config)) {
		config = {};
	}

	if(iDef(args.configClass)) {
		config = {...new args.configClass(), ...config};
	}

	// process the config for any properties or property refrences
	const literalConfig = PropertyProvider.resolveFields(config, state);

	let sharedState = state.active.panelState[instance];

	// if there is no shared state in the store, initalize it to either a default shared state supplied to the panel decorator
	if(nDef(sharedState)) {
		sharedState = iDef(args.sharedState) ? args.sharedState : {};
	}

	// if (args.id === "forumSubscription") {
	// 	sharedState = { ...sharedState, forumId: config.forumId };
	// }

	const isRegistered = state.active.panels.includes(instance);

	return {
		instance,
		id: config.id,
		config: literalConfig,
		panelClass: panel,
		panelId: args.id,
		layoutEdit: state.edit.layout,
		pageParams: args.pageParams ? queryString.parse(state.router.location.search) : {},
		globalState: sharedState,
		isRegistered,
		editingEnabled: state.edit.enabled,
		language: state.user.language,
		locale: state.user.locale,
		timeZone: state.user.timeZone,
		trackPaginationVal: state.params.trackPaginationVal,
		itemsToShow: state.pageSize.itemsToShow,
		cartItems: state.params.cartItems,
		quantityInCart: state.params.quantityInCart,
		discountResponse: state.params.discountResponse,
		cartUserDetails: state.params.cartUserDetails,
		shippableUserDetails: state.params.shippableUserDetails,
		cartItemsGroupBuy: state.params.cartItemsGroupBuy,
		searchText: args.pageParams && queryString.parse(state.router.location.search) ?
		 			queryString.parse(state.router.location.search).searchText : '',
		searchIncludesTag: args.pageParams && queryString.parse(state.router.location.search) ? 
			queryString.parse(state.router.location.search).searchIncludesTag : false,
		forumId: state.params.forumId,
	};
};

const mapDispatchToProps = (dispatch, ownProps) => {
	 const ad = {
		actions: {
			...bindActionCreators(activeActions as any, dispatch),
			...bindActionCreators(paramsActions as any, dispatch),
			...bindActionCreators(userActions as any, dispatch),
			...bindActionCreators(cartAction as any, dispatch),
			...bindActionCreators(pageSizeActions as any, dispatch)
		}
	};

 	const set = ad.actions.setGlobalState;

 	ad.actions.setGlobalState = (state) => set(ownProps.instance, state);

	 return ad;
};

interface IPanelWrapperProps {
	panelClass: Class<any>;
	panelId: string;
	instance: string;
	actions: any;
	config: PanelGeneralConfig;
	panel?: JSX.Element;
	isRegistered?: boolean;
	active?: object;
	variants?: object;
	panelViews?: object;
	forumId?: string;
}


class PanelWrapper extends React.Component<IPanelWrapperProps> {

	// use deep compare for props for now to prevent infinite updating
	public shouldComponentUpdate(nextProps) {
		return !isEqual(this.props, nextProps);
	}

	@autobind
	private registerView(viewId: string, viewKey: string) {
		this.props.actions.addActiveView(viewId, viewKey, this.props.instance);
	}

	// TODO move this into a util or the reize condition type file
	@autobind
	private monitorResize() {
		const { config: { __permissions }} = this.props;

		// TODO deep check the permisions object for sized conditions in containers
		return __permissions &&
			WindowSizeCondition.type === __permissions['type'];
			// Object.keys(__permissions).length > 0 &&
			// flattMapDeep(__permissions, (p) => p.type)
			// .includes(WindowSizeCondition.type);
	}

	public componentDidMount() {
		// if(this.monitorResize()) {
		// window.addEventListener("resize", () => this.forceUpdate());
		// }
	}

		public componentWillUnmount() {
		// if(this.monitorResize()) {
	    // window.removeEventListener("resize", () => this.forceUpdate());
		// }
		}

	public render() {
		const { config, instance, panelId } = this.props;

		const PanelComponent = (props) => {

			const panelElement = React.createElement(props.panelClass, {...props, parent: last(props.ancestors)});

			let wrapperProps: object = {
				'id': 'panel:' + instance,
				'data-panel-id': panelId
			};

			if (config.__attrs && config.__attrs.length) {
				wrapperProps = {...toAttributesObject(config.__attrs), ...wrapperProps};
			}

			// id prop for navigation to specific panel
			return (
				<div {...wrapperProps}>
					{config.__border ? (
						<BoxView variant={BoxVariant.PANEL} viewKey="__border" title={
							<React.Fragment>
								{config.__title ? setPageHeading(config.__title) : ''}{/*added for accessibility */}
								<Label k={`panel.${instance}.__title`} v={config.__title} />
							</React.Fragment>
						}>{panelElement}
						</BoxView>
					) : panelElement}
				</div>
			);
		};

		return (
			<ErrorBoundary type={ErrorSource.PANEL} location={this.props.instance}>
				<AncestorProvider {...this.props}>
					{(props) => (

						<PanelContext.Provider value={{instance:  this.props.instance, panelId: this.props.panelId, registerView: this.registerView, editingEnabled: props.editingEnabled}}>

							{props.editingEnabled ? (
								<ConnectedEditWrapper {...{...props, parent: last(props.ancestors)}} children={PanelComponent(props)} />
							) : PanelComponent(props)}

						</PanelContext.Provider>
					)}
				</AncestorProvider>
			</ErrorBoundary>
		);
	}
}

interface IEditWrapperProps extends IPanelWrapperProps {
	isEditing?: boolean;
	editorActions?: typeof configActions & typeof editActions;
	panelViews?: object;
	views?: object;
}

@ContextMenuTarget
class EditWrapper extends React.Component<IEditWrapperProps> {

	public componentDidMount() {
		// only register if not yet registered
		if (this.props.isRegistered) {
			return;
		}
		this.props.actions.addActivePanel(this.props.instance);
	}

	public render() {
		return (
			<div className={this.props.isEditing ? "editing-active" : undefined}>
				{this.props.children}
			</div>
		);
	}

	public renderContextMenu() {
		const viewsList = Object.entries(this.props.active || {});
		return (
			<div className={Classes.DARK} style={{maxHeight: 300, overflowY: 'scroll'}}>
				<Menu>
					<MenuDivider key="__main-title" title={`Panel: ${this.props.panelId}`} />
					<MenuItem
						key="__main-edit"
						text="Edit"
						icon={IconNames.WRENCH}
						onClick={() => this.props.editorActions.editSetActive({type: "panel", instance: this.props.instance})}
					/>
					<MenuDivider key="__main-views" title="Views" />
					{
						viewsList.length > 0 ? viewsList.map(([viewKey, viewId]) => {

							const variants = Object.entries(this.props.views[viewId] || {});

							const sVariantInstance: string = this.props.panelViews[getPanelViewKey(this.props.instance, viewKey)];

							return (
								<MenuItem key={viewKey} text={viewKey} label={'(' + viewId + ')'} intent={sVariantInstance ? Intent.SUCCESS : Intent.NONE}>
									{
										variants.length > 0 ? variants.map(([vi, vc]) => {

											return (
												<MenuItem
													icon={vi === sVariantInstance ? IconNames.TICK : undefined}
													intent={vi === sVariantInstance ? Intent.SUCCESS : Intent.NONE}
													key={vi}
													onClick={this.selectVariant(this.props.instance, viewKey, vi)}
													text={vc['__editName'] || vc[EDIT_NAME] || "Untitled Variant"}
												/>
											);
										}) : (
											<MenuItem disabled={true} key="none-available" text="No Variants Available" />
										)
									}
									{
										sVariantInstance && (
											<React.Fragment key="remove-panel-view">
												<MenuDivider />
												<MenuItem
													icon={IconNames.SMALL_CROSS}
													intent={Intent.WARNING}
													text="Back to default"
													onClick={this.removeVariantSelection(this.props.instance, viewKey)}
												/>
											</React.Fragment>
										)
									}
								</MenuItem>
							);
						}) : (
							<MenuItem key="__main-no-views" disabled={true} text="No Active Views" />
						)
					}
				</Menu>
			</div>
		);
	}

	private selectVariant = (panelInstance: string, viewKey: string, viewInstance: string) => () => {
		this.props.editorActions.configPanelViewAdd(getPanelViewKey(panelInstance, viewKey), viewInstance);
	}

	private removeVariantSelection = (panelInstance: string, viewKey: string) => () => {
		this.props.editorActions.configPanelViewRemove(getPanelViewKey(panelInstance, viewKey));
	}
}

function mapEditWrapperDispatch(dispatch: any) {
	return {
		editorActions: {
			...bindActionCreators(configActions as any, dispatch),
			...bindActionCreators(editActions as any, dispatch)
		}
	};
}

function mapEditWrapperProps({config: { panelViews, views }, active, edit} = initialState, ownProps: IEditWrapperProps) {

	return {
		panelViews,
		views,
		isEditing: edit.active ? edit.active.instance === ownProps.instance : false,
		active: active.views[ownProps.instance],
	};
}

const ConnectedEditWrapper = connect(mapEditWrapperProps, mapEditWrapperDispatch)(EditWrapper);



// WEBPACK FOOTER //
// ./src/core/decorators/Panel.tsx